
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import config from '../config';
import '../styles/indox.css';
import '../styles/messageSearch.css';
import Notification from '../components/Notification';
import MessageSearch from '../components/MessageSearch';
import Cookies from 'js-cookie';
import InfiniteScroll from 'react-infinite-scroll-component';
import { InboxSkeleton } from '../components/Skeletons';
import { useMessageSearch } from '../hooks/useMessageSearch';

// const formatDate = (timestamp) => { // Commented out - unused function
//     const date = new Date(timestamp);
//     const now = new Date();
//     const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
//     const diffInDays = Math.floor(diffInHours / 24);

//     // If less than 24 hours ago
//     if (diffInHours < 24) {
//         if (diffInHours < 1) {
//             const diffInMinutes = Math.floor((now - date) / (1000 * 60));
//             return diffInMinutes < 1 ? 'À l\'instant' : `Il y a ${diffInMinutes}m`;
//         }
//         return `Il y a ${diffInHours}h`;
//     }

//     // If less than 7 days ago
//     if (diffInDays < 7) {
//         return `Il y a ${diffInDays}j`;
//     }

//     // Otherwise return formatted date
//     return date.toLocaleDateString('fr-FR', {
//         year: 'numeric',
//         month: 'short',
//         day: 'numeric'
//     });
// };

// MessageItem component moved to EnhancedMessageItem inside EnhancedMessaging for proper context access

const EnhancedMessaging = ({ onLoginClick }) => {
    const { userId } = useParams();
    
    // Use the advanced message search hook with pagination
    const {
        query,
        setQuery,
        suggestions,
        showSuggestions,
        setShowSuggestions,
        searchHistory,
        conversations: filteredConversations,
        allConversations,
        isLoading: searchLoading,
        isLoadingMore,
        error: searchError,
        activeTab,
        setActiveTab,
        addToSearchHistory,
        clearSearchHistory,
        loadConversations,
        loadMoreConversations,
        refreshConversations,
        applyFiltersAndSort,
        stats,
        hasMore
    } = useMessageSearch();

    // Track if this is the very first load
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    // Mark initial load as complete after first successful load
    useEffect(() => {
        if (!searchLoading && allConversations.length > 0 && isInitialLoad) {
            setIsInitialLoad(false);
        }
    }, [searchLoading, allConversations.length, isInitialLoad]);

    // Legacy state for compatibility
    const [notification, setNotification] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [sortBy, setSortBy] = useState('date');
    const [filterBy, setFilterBy] = useState('all');
    const [newMessageIds, setNewMessageIds] = useState(new Set());
    const [lastSeenTimestamp, setLastSeenTimestamp] = useState(Date.now());
    const socketRef = useRef(null);
    const wsReconnectAttemptsRef = useRef(0);
    const wsReloadDebounceRef = useRef(null);
    const MAX_WS_RECONNECT_ATTEMPTS = 5;

    // Redux products removed as it was not being used

    // Tab definitions - Enhanced like mobile app
    const tabs = [
        { id: 'all', label: 'Tous les Messages', count: stats.totalConversations, icon: '💬' },
        { id: 'my-products', label: 'Mes Produits', count: stats.myProductsCount, icon: '🏪' },
        { id: 'contacted-products', label: 'Produits Contactés', count: stats.contactedProductsCount, icon: '📱' },
        { id: 'unread', label: 'Non Lus', count: stats.unreadCount, icon: '📬' },
        { id: 'recent', label: 'Récents', count: null, icon: '🕒' }
    ];

    // Handle search suggestion selection
    const handleSuggestionSelect = (selectedQuery) => {
        setQuery(selectedQuery);
        addToSearchHistory(selectedQuery);
        setShowSuggestions(false);
    };

    // Clear search
    const clearSearch = () => {
        setQuery('');
        setActiveTab('all');
    };

    // Check for new messages (like mobile app)
    // CRITICAL: Only mark conversations as "new" where current user is the RECIPIENT
    const checkForNewMessages = useCallback((conversations) => {
        const newIds = new Set();
        
        conversations.forEach(conversation => {
            // Method 1: Check unread_count (preferred - server provides this)
            if (conversation.unread_count && conversation.unread_count > 0) {
                newIds.add(conversation.id);
            }
            // Method 2: Fallback - Check if last_sender is NOT current user AND message is recent
            else if (conversation.last_sender_id && conversation.last_sender_id !== parseInt(userId)) {
                const messageTime = new Date(conversation.last_update_date).getTime();
                // Check if message is newer than last seen timestamp
                if (messageTime > lastSeenTimestamp && conversation.product_message_count > 0) {
                    newIds.add(conversation.id);
                }
            }
            // Method 3: Final fallback - original logic (if no sender info available)
            else if (!conversation.last_sender_id && !conversation.unread_count) {
                const messageTime = new Date(conversation.last_update_date).getTime();
                if (messageTime > lastSeenTimestamp && conversation.product_message_count > 0) {
                    newIds.add(conversation.id);
                }
            }
        });
        
        setNewMessageIds(newIds);
    }, [lastSeenTimestamp, userId]);

    // Update last seen timestamp when user interacts
    const markAsSeen = useCallback(() => {
        setLastSeenTimestamp(Date.now());
        setNewMessageIds(new Set());
    }, []);

    // Check if conversation has new messages
    const isNewMessage = useCallback((conversationId) => {
        return newMessageIds.has(conversationId);
    }, [newMessageIds]);

    // Memoized filtered conversations for display with additional filters
    const displayConversations = useMemo(() => {
        // Always use filteredConversations from the hook as it handles tab filtering
        // The hook will return all conversations when no search query and activeTab is 'all'
        return applyFiltersAndSort(filteredConversations, filterBy, sortBy);
    }, [filteredConversations, filterBy, sortBy, applyFiltersAndSort]);

    // Format date for display
    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInHours / 24);

        if (diffInHours < 24) {
            if (diffInHours < 1) {
                const diffInMinutes = Math.floor((now - date) / (1000 * 60));
                return diffInMinutes < 1 ? 'À l\'instant' : `Il y a ${diffInMinutes}m`;
            }
            return `Il y a ${diffInHours}h`;
        }

        if (diffInDays < 7) {
            return `Il y a ${diffInDays}j`;
        }

        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    useEffect(() => {
        const token = Cookies.get('authToken');
        if (!token) {
            onLoginClick(); 
            return;
        }
        
        // Load conversations using the search hook
        loadConversations();

        // Initialize WebSocket connection for real-time message count updates
        const initializeWebSocket = () => {
            if (socketRef.current) {
                socketRef.current.close();
            }

            const newSocket = new WebSocket(`${config.websocketUrl}chat/message_count/${userId}/`);
            socketRef.current = newSocket;
            
            newSocket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);

                    // Handle message count updates (real-time notification)
                    if (data.type === 'message_count' || data.type === 'count_update') {
                        const messageData = data.data || {};
                        
                        // OPTIMIZATION: Only reload if there are actually new messages
                        // and debounce is reasonable - increase to 5 seconds to reduce visual flashing
                        if (messageData.total_unread_messages > 0) {
                            // Clear existing debounce timer
                            if (wsReloadDebounceRef.current) {
                                clearTimeout(wsReloadDebounceRef.current);
                            }
                            
                            // Debounce reload by 5 seconds to reduce flashing
                            // This gives time for multiple rapid updates to batch
                            wsReloadDebounceRef.current = setTimeout(() => {
                                refreshConversations(); // Use refresh to reload page 1 silently
                            }, 5000);
                        }
                    }
                    
                    // Legacy handling - full conversation list (deprecated)
                    else if (data && data.results && Array.isArray(data.results)) {
                        // Check for new messages
                        try {
                            checkForNewMessages(data.results);
                        } catch (parseError) {
                            // Silent fail
                        }
                        
                        // Debounced refresh - 5 seconds
                        if (wsReloadDebounceRef.current) {
                            clearTimeout(wsReloadDebounceRef.current);
                        }
                        wsReloadDebounceRef.current = setTimeout(() => {
                            refreshConversations(); // Use refresh to reload page 1 silently
                        }, 5000);
                    }
                } catch (parseError) {
                    // Silent fail - don't pollute console
                }
            };

            // Handle WebSocket close event with exponential backoff
            newSocket.onclose = (event) => {
                if (wsReconnectAttemptsRef.current < MAX_WS_RECONNECT_ATTEMPTS) {
                    const backoffTime = Math.min(Math.pow(2, wsReconnectAttemptsRef.current) * 1000, 30000); // Max 30s
                    setTimeout(() => {
                        wsReconnectAttemptsRef.current++;
                        initializeWebSocket();
                    }, backoffTime);
                }
            };

            newSocket.onerror = () => {
                // Silent fail - errors logged on close
            };

            newSocket.onopen = () => {
                wsReconnectAttemptsRef.current = 0; // Reset counter on successful connection
            };
        };

        // Delay WebSocket initialization to prioritize initial data load
        const wsTimeout = setTimeout(() => {
            initializeWebSocket();
        }, 500); // 500ms delay

        // Clean up
        return () => {
            clearTimeout(wsTimeout);
            if (wsReloadDebounceRef.current) {
                clearTimeout(wsReloadDebounceRef.current);
            }
            if (socketRef.current) {
                socketRef.current.close(1000, 'Component unmounting');
                socketRef.current = null;
            }
        };
    }, [onLoginClick, userId, loadConversations, checkForNewMessages, refreshConversations]);

    const handleNotificationClose = useCallback(() => {
        setNotification('');
    }, []);

    // Enhanced Message Item Component with better mobile support
    // Delete conversation handler
    const handleDeleteConversation = useCallback(async (conversationId, event) => {
        // Prevent navigation
        event.preventDefault();
        event.stopPropagation();
        
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette conversation? Cette action ne peut pas être annulée.')) {
            return;
        }

        try {
            const token = Cookies.get('authToken');
            const response = await fetch(
                `${config.backendUrl}/messaging/conversations/${conversationId}/delete/`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`,
                    },
                }
            );

            if (response.ok) {
                // Refresh conversations after deletion
                await refreshConversations();
                setNotification({
                    message: 'Conversation supprimée avec succès',
                    type: 'success',
                    header: 'Suppression réussie'
                });
            } else {
                const errorData = await response.json();
                setNotification({
                    message: errorData.error || 'Erreur lors de la suppression',
                    type: 'error',
                    header: 'Erreur'
                });
            }
        } catch (error) {
            setNotification({
                message: 'Erreur réseau lors de la suppression',
                type: 'error',
                header: 'Erreur'
            });
        }
    }, [refreshConversations]);

    const EnhancedMessageItem = React.memo(({ chat, userId, formatDate, isNewMessage }) => {
        // Use UUID with fallback to ID for all identifiers
        const productIdentifier = chat.product_uuid || chat.product_id;
        const userIdentifier = userId; // This is already passed as UUID from parent
        
        // Build query params with conversation details to avoid API calls
        const params = new URLSearchParams({
            productName: chat.product_name || '',
            productPrice: chat.product_price?.toString() || '',
            productCurrency: chat.product_currency || 'CDF',
            productImage: chat.product_image || '',
            categoryName: chat.category_name || '',
            categoryId: chat.category_id?.toString() || '',
            ownerName: `${chat.sender_first_name || ''} ${chat.sender_last_name || ''}`,
            ownerPhone: chat.owner_phone || '',
            ownerProfilePicture: chat.sender_profile_picture || '',
            messageCount: chat.message_count?.toString() || '0',
            lastUpdate: chat.last_update_date || '',
            productOwnerId: chat.product_owner_id?.toString() || '',
            productOwnerUuid: chat.product_owner_uuid || '',
            recipientId: chat.other_user_id?.toString() || chat.sender_id?.toString() || '',
            recipientUuid: chat.other_user_uuid || chat.sender_uuid || ''
        });
        
        return (
            <div className="message-item enhanced conversation-item-wrapper" role="listitem">
                <Link 
                    to={`/messages/chatroom/${chat.id}/${productIdentifier}/${userIdentifier}?${params.toString()}`} 
                    className="message-link"
                    aria-label={`Ouvrir la conversation avec ${chat.sender_first_name} ${chat.sender_last_name} à propos de ${chat.product_name}`}
                    style={{ 
                        textDecoration: 'none',
                        display: 'block',
                        width: '100%',
                        minHeight: '60px',
                        padding: '0'
                    }}
                    onClick={markAsSeen}
                >
                <div className="message-card" style={{ position: 'relative' }}>
                    <div className="avatar" aria-hidden="true">
                        {chat.sender_first_name ? chat.sender_first_name[0].toUpperCase() : ''}
                        {chat.sender_last_name ? chat.sender_last_name[0].toUpperCase() : ''}
                        {chat.product_message_count > 0 && (
                            <div className="avatar-badge">
                                {chat.product_message_count}
                            </div>
                        )}
                    </div>
                    <div className="message-content">
                        <div className="message-header">
                            <span className={`message-sender ${chat.product_message_count > 0 ? 'unread' : ''}`}>
                                {`${chat.sender_first_name || ''} ${chat.sender_last_name || ''}`.trim() || 'Utilisateur'}
                            </span>
                            {isNewMessage && (
                                <span className="new-message-label">NOUVEAU</span>
                            )}
                        </div>
                        <p className="product-name" title={chat.product_name}>
                            {chat.product_name || 'Produit non spécifié'}
                        </p>
                        <div className="message-info">
                            <span className="message-username">@{chat.sender_username || 'inconnu'}</span>
                            <span className="bullet" aria-hidden="true">•</span>
                            <span className="message-count">
                                {chat.message_count || 0} message{(chat.message_count || 0) !== 1 ? 's' : ''}
                                {chat.product_message_count > 0 && (
                                    <span className="unread-indicator">
                                        {` (${chat.product_message_count} non lu${chat.product_message_count !== 1 ? 's' : ''})`}
                                    </span>
                                )}
                            </span>
                        </div>
                    </div>
                    <div className="message-meta">
                        <span className="message-date">
                            {formatDate(chat.last_update_date)}
                        </span>
                        {chat.product_message_count > 0 && (
                            <div className="unread-status">
                                <span 
                                    className="unread-count pulse"
                                    aria-label={`${chat.product_message_count} message${chat.product_message_count !== 1 ? 's' : ''} non lu${chat.product_message_count !== 1 ? 's' : ''}`}
                                >
                                    {chat.product_message_count}
                                </span>
                                <div className="unread-dot"></div>
                            </div>
                        )}
                    </div>
                </div>
            </Link>
            {/* Delete button - shown on hover */}
            <button
                className="delete-conversation-btn"
                onClick={(e) => handleDeleteConversation(chat.id, e)}
                aria-label="Supprimer la conversation"
                title="Supprimer la conversation"
            >
                <span className="delete-icon">🗑️</span>
            </button>
        </div>
        );
    }, (prevProps, nextProps) => {
        // Custom comparison: only re-render if this specific conversation changed
        return (
            prevProps.chat.id === nextProps.chat.id &&
            prevProps.chat.product_message_count === nextProps.chat.product_message_count &&
            prevProps.chat.last_update_date === nextProps.chat.last_update_date &&
            prevProps.chat.message_count === nextProps.chat.message_count &&
            prevProps.isNewMessage === nextProps.isNewMessage
        );
    });

    if (searchLoading && isInitialLoad) {
        return <InboxSkeleton />;
    }

    if (searchError) {
        return (
            <div className="messaging-container enhanced indox-error-container">
                <div className="error-state enhanced">
                    <div className="error-icon" aria-hidden="true">⚠️</div>
                    <h2>Erreur de chargement</h2>
                    <p>Une erreur s'est produite lors du chargement de vos conversations.</p>
                    <p className="indox-error-message">{searchError}</p>
                    <div className="error-actions">
                        <button 
                            className="action-button primary" 
                            onClick={loadConversations}
                            aria-label="Réessayer le chargement des conversations"
                        >
                            🔄 Réessayer
                        </button>
                        <button 
                            className="action-button secondary" 
                            onClick={() => window.location.reload()}
                            aria-label="Actualiser la page"
                        >
                            ↻ Actualiser la page
                        </button>
                    </div>
                    {/* Debug info */}
                    {config.debug && (
                        <div className="debug-info-panel">
                            <h4>Informations de débogage :</h4>
                            <div className="debug-details">
                                <p><strong>URL Backend:</strong> {config.backendUrl}</p>
                                <p><strong>Token Auth:</strong> {Cookies.get('authToken') ? 'Présent' : 'Manquant'}</p>
                                <p><strong>Erreur:</strong> {searchError}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="messaging-container enhanced" style={{ maxWidth: '100%', overflowX: 'hidden' }}>
            {/* Enhanced Header with Search and Filters */}
            <div className="messaging-header enhanced">
                <div className="header-top">
                    <h1>Boîte de réception</h1>
                    <div className="header-controls">
                        {config.debug && (
                            <div className="debug-info">
                                {displayConversations.length} conversations chargées
                            </div>
                        )}
                        <button 
                            className="filter-toggle-btn"
                            onClick={() => setShowFilters(!showFilters)}
                            aria-label="Basculer les filtres"
                        >
                            <span className="filter-icon">⚙️</span>
                            <span>Filtres</span>
                        </button>
                    </div>
                </div>
                
                {/* Advanced Search Component */}
                <div className="search-section">
                    <MessageSearch
                        query={query}
                        setQuery={setQuery}
                        suggestions={suggestions}
                        searchHistory={searchHistory}
                        showSuggestions={showSuggestions}
                        setShowSuggestions={setShowSuggestions}
                        onSuggestionSelect={handleSuggestionSelect}
                        clearHistory={clearSearchHistory}
                        placeholder="Rechercher par nom, nom d'utilisateur ou produit..."
                    />
                    
                    {/* Search loading indicator - only show when searching or refreshing (not initial load) */}
                    {(query && searchLoading) || (searchLoading && !isInitialLoad) && (
                        <div className="search-loading-indicator">
                            <div className="search-spinner"></div>
                            <span>Recherche en cours...</span>
                        </div>
                    )}
                    
                    {query && (
                        <button className="clear-search-btn" onClick={clearSearch}>
                            <span>✕</span> Effacer la recherche
                        </button>
                    )}
                </div>

                {/* Enhanced Filters Panel */}
                {showFilters && (
                    <div className="filters-panel enhanced">
                        <div className="filter-section">
                            <h4 className="filter-section-title">🔄 Trier par</h4>
                            <div className="filter-buttons">
                                <button 
                                    className={`filter-button ${sortBy === 'date' ? 'active' : ''}`}
                                    onClick={() => setSortBy('date')}
                                >
                                    <span className="filter-icon">🕒</span>
                                    <span>Date</span>
                                </button>
                                <button 
                                    className={`filter-button ${sortBy === 'name' ? 'active' : ''}`}
                                    onClick={() => setSortBy('name')}
                                >
                                    <span className="filter-icon">👤</span>
                                    <span>Nom</span>
                                </button>
                                <button 
                                    className={`filter-button ${sortBy === 'unread' ? 'active' : ''}`}
                                    onClick={() => setSortBy('unread')}
                                >
                                    <span className="filter-icon">📬</span>
                                    <span>Non lus</span>
                                </button>
                            </div>
                        </div>
                        <div className="filter-section">
                            <h4 className="filter-section-title">🔍 Filtrer par</h4>
                            <div className="filter-buttons">
                                <button 
                                    className={`filter-button ${filterBy === 'all' ? 'active' : ''}`}
                                    onClick={() => setFilterBy('all')}
                                >
                                    <span className="filter-icon">📋</span>
                                    <span>Tous</span>
                                </button>
                                <button 
                                    className={`filter-button ${filterBy === 'unread' ? 'active' : ''}`}
                                    onClick={() => setFilterBy('unread')}
                                >
                                    <span className="filter-icon">📩</span>
                                    <span>Non lus</span>
                                </button>
                                <button 
                                    className={`filter-button ${filterBy === 'read' ? 'active' : ''}`}
                                    onClick={() => setFilterBy('read')}
                                >
                                    <span className="filter-icon">📭</span>
                                    <span>Lus</span>
                                </button>
                            </div>
                        </div>
                        <div className="filter-actions">
                            <button 
                                className="reset-filters-btn"
                                onClick={() => {
                                    setSortBy('date');
                                    setFilterBy('all');
                                    setActiveTab('all');
                                }}
                            >
                                🔄 Réinitialiser
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Enhanced Tab Navigation */}
            <div className="tab-navigation enhanced" role="tablist" aria-label="Filtres de messages">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        aria-controls={`messages-panel-${tab.id}`}
                    >
                        <span className="tab-icon">{tab.icon}</span>
                        <span className="tab-label">{tab.label}</span>
                        {tab.count !== null && tab.count > 0 && (
                            <span className={`tab-count ${tab.id === 'unread' && tab.count > 0 ? 'pulse' : ''}`} aria-label={`${tab.count} messages`}>
                                {tab.count}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Search Results Info */}
            {query && (
                <div className="search-info">
                    <div className="search-results-header">
                        <span className="search-icon">🔍</span>
                        <p>
                            {displayConversations.length === 0
                                ? `Aucune conversation trouvée pour "${query}"`
                                : `${displayConversations.length} conversation${displayConversations.length !== 1 ? 's' : ''} trouvée${displayConversations.length !== 1 ? 's' : ''} pour "${query}"`
                            }
                        </p>
                    </div>
                </div>
            )}

            {/* Message List */}
            <div 
                className="messages-list-container" 
                role="tabpanel" 
                id={`messages-panel-${activeTab}`}
                aria-labelledby={`tab-${activeTab}`}
            >
                {/* Show skeleton only on very first load */}
                {searchLoading && isInitialLoad ? (
                    <div className="messages-list enhanced" role="list">
                        {/* Show 3 skeleton items */}
                        {[1, 2, 3].map(i => (
                            <div key={`skeleton-${i}`} className="skeleton-message-item">
                                <div className="skeleton-avatar"></div>
                                <div className="skeleton-content">
                                    <div className="skeleton-line skeleton-title"></div>
                                    <div className="skeleton-line skeleton-subtitle"></div>
                                    <div className="skeleton-line skeleton-text"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : displayConversations.length > 0 ? (
                    <>
                        <div className="messages-list enhanced" role="list" aria-label="Conversations">
                            {/* Render all conversations */}
                            {displayConversations.map((chat) => (
                                <EnhancedMessageItem 
                                    key={chat.id} 
                                    chat={chat} 
                                    userId={userId}
                                    formatDate={formatDate}
                                    isNewMessage={isNewMessage(chat.id)}
                                />
                            ))}
                        </div>
                        
                        {/* Load More Button - only show when not searching */}
                        {!query && hasMore && (
                            <div className="load-more-container" style={{ padding: '20px', textAlign: 'center' }}>
                                <button
                                    className="action-button primary"
                                    onClick={loadMoreConversations}
                                    disabled={isLoadingMore}
                                    style={{
                                        padding: '12px 24px',
                                        fontSize: '16px',
                                        cursor: isLoadingMore ? 'not-allowed' : 'pointer',
                                        opacity: isLoadingMore ? 0.6 : 1
                                    }}
                                >
                                    {isLoadingMore ? (
                                        <>
                                            <span className="loading-spinner" style={{ marginRight: '8px' }}>⏳</span>
                                            Chargement...
                                        </>
                                    ) : (
                                        <>
                                            <span style={{ marginRight: '8px' }}>⬇️</span>
                                            Charger plus de conversations
                                        </>
                                    )}
                                </button>
                                <p style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>
                                    {displayConversations.length} sur {stats.totalConversations} conversations
                                </p>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="no-messages enhanced" role="status" aria-live="polite">
                        {query ? (
                            <div className="no-search-results">
                                <div className="empty-state-icon" aria-hidden="true">🔍</div>
                                <h3>Aucune conversation trouvée</h3>
                                <p>Essayez d'ajuster vos termes de recherche ou effacez la recherche pour voir toutes les conversations.</p>
                                <button 
                                    onClick={clearSearch} 
                                    className="action-button primary"
                                    aria-label="Effacer la recherche et afficher toutes les conversations"
                                >
                                    Effacer la recherche
                                </button>
                            </div>
                        ) : (
                            <div className="no-conversations">
                                <div className="empty-state-icon" aria-hidden="true">💬</div>
                                <h3>Pas encore de messages</h3>
                                <p>Commencez une conversation en contactant quelqu'un à propos de son produit.</p>
                                <button 
                                    className="action-button secondary" 
                                    onClick={() => window.location.href = '/products'}
                                    aria-label="Parcourir les produits pour commencer des conversations"
                                >
                                    Parcourir les Produits
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Notification */}
            {notification && (
                <Notification 
                    message={notification} 
                    type="error" 
                    onClose={handleNotificationClose} 
                />
            )}
        </div>
    );
};

export default React.memo(EnhancedMessaging);
